import { Button } from "@dropbox/dig-components/buttons";
import { Text, Title } from "@dropbox/dig-components/typography";
import { Box, Stack } from "@dropbox/dig-foundations";
import { BowlEmptySpot } from "@dropbox/dig-illustrations";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_DashSelfServeOnboardingSupportButton } from "@mirage/analytics/events/types/click_dash_self_serve_onboarding_support_button";
import { PAP_Click_DashSelfServeOnboardingTryAgainButton } from "@mirage/analytics/events/types/click_dash_self_serve_onboarding_try_again_button";
import { PAP_Shown_DashSelfServeOnboardingErrorPage } from "@mirage/analytics/events/types/shown_dash_self_serve_onboarding_error_page";
import { PAP_Shown_DashSelfServeOnboardingSupportButton } from "@mirage/analytics/events/types/shown_dash_self_serve_onboarding_support_button";
import { getSupportUrl } from "@mirage/mosaics/Feedback/support-url";
import { openURL } from "@mirage/service-platform-actions";
import { useDoOnceOnMount } from "@mirage/shared/hooks/useDoOnceOnMount";
import Sentry from "@mirage/shared/sentry";
import i18n from "@mirage/translations";
import { useState } from "react";
import styles from "./SelfServeOnboardingErrorPage.module.css";

type Props = {
  onProvisionDash: () => Promise<boolean>;
};

export const SelfServeOnboardingErrorPage = ({ onProvisionDash }: Props) => {
  const [displayTryAgainButton, setDisplayTryAgainButton] =
    useState<boolean>(true);
  const [isProvisioning, setIsProvisioning] = useState<boolean>(false);
  const { reportPapEvent } = useMirageAnalyticsContext();

  useDoOnceOnMount(() => {
    reportPapEvent(
      PAP_Shown_DashSelfServeOnboardingErrorPage({
        featureLine: "onboarding",
      }),
    );
  });

  const handleProvisionDash = async () => {
    setIsProvisioning(true);
    reportPapEvent(
      PAP_Click_DashSelfServeOnboardingTryAgainButton({
        featureLine: "onboarding",
      }),
    );
    const success = await onProvisionDash();
    setIsProvisioning(false);
    if (!success) {
      setDisplayTryAgainButton(false);
      Sentry.captureMessage(
        `[SelfServeOnboardingErrorPage] Error provisioning Dash on Self-Serve Onboarding Error Page`,
      );
      reportPapEvent(
        PAP_Shown_DashSelfServeOnboardingSupportButton({
          featureLine: "onboarding",
        }),
      );
    }
  };

  const handleContactSupport = () => {
    reportPapEvent(
      PAP_Click_DashSelfServeOnboardingSupportButton({
        featureLine: "onboarding",
      }),
    );
    openURL(getSupportUrl());
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      flexGrow={1}
    >
      <Stack justify="center" align="center" display="flex" gap="72px">
        <Stack align="center" display="flex">
          <Title
            size="large"
            weightVariant="emphasized"
            className={styles.header}
          >
            {i18n.t("self_serve_error_page_title")}
          </Title>
          <Text
            variant="paragraph"
            size="large"
            color="subtle"
            className={styles.subHeader}
          >
            {i18n.t("self_serve_error_page_description")}
          </Text>
        </Stack>
        <Box className={styles.spotImage}>
          <BowlEmptySpot />
        </Box>
        {displayTryAgainButton ? (
          <Button
            className={styles.actionButton}
            data-testid="tryAgainButton"
            size="xlarge"
            variant="outline"
            fullWidth
            onClick={handleProvisionDash}
            isLoading={isProvisioning}
          >
            <span className={styles.actionButtonLabel}>
              {i18n.t("try_again")}
            </span>
          </Button>
        ) : (
          <Button
            className={styles.actionButton}
            data-testid="contactSupportButton"
            size="xlarge"
            variant="outline"
            fullWidth
            onClick={handleContactSupport}
          >
            <span className={styles.actionButtonLabel}>
              {i18n.t("contact_support")}
            </span>
          </Button>
        )}
      </Stack>
    </Box>
  );
};
